import { useCallback, useEffect, useRef } from 'react';

import { useLogger } from '@/shared/hooks/useLogger';

import { useHandleError } from '../../../error-boundary-context/context';
import { AppErrors } from '../../../error-boundary-context/error-map/map';

interface UseHandleAndLogErrorProps<ErrorType> {
    onError?: (error: ErrorType) => void;
    errorToRaise: (error: ErrorType) => AppErrors;
    errorMessage: (error: ErrorType) => string;
    isFatal: (error: ErrorType) => boolean;
}

export const useHandleAndLogError = <ErrorType extends Omit<Error | ErrorEvent, 'name'>>({
    onError,
    errorMessage,
    errorToRaise,
    isFatal,
}: UseHandleAndLogErrorProps<ErrorType>) => {
    const handleError = useHandleError();
    const logger = useLogger();

    const cancelledRef = useRef<boolean>(false);

    useEffect(
        () => () => {
            cancelledRef.current = true;
        },
        []
    );

    const onErrorWrapped = useCallback(
        (error: ErrorType) => {
            if (cancelledRef.current) {
                return;
            }

            if (isFatal(error)) {
                handleError(errorToRaise(error), { playerError: error });
            }

            onError?.(error);

            logger.error({
                message: errorMessage(error),
                error: { ...error, name: errorMessage(error) },
            });
        },
        [errorMessage, errorToRaise, handleError, isFatal, logger, onError]
    );

    return onErrorWrapped;
};
